import React from "react";
import { Column } from "primereact/column";
import { DataTableStateEvent } from "primereact/datatable";
import { PaginatorPageChangeEvent } from "primereact/paginator";

import * as Styled from "./results-table.styled";
import { ReactComponent as NoSortIcon } from "../assets/noSort.svg";
import { ReactComponent as UpSortIcon } from "../assets/upSort.svg";
import { ReactComponent as RevSortIcon } from "../assets/revSort.svg";
import {
  TablePaginationHeader,
  TablePaginatorFooter,
} from "../../../shared/ui/data-table/ui/table-paginator-footer";

export const SEARCH_RESULTS_ROWS_PER_PAGE = 100;
export const SEARCH_RESULTS_ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

export interface SearchResultsColumn<T> {
  field: string;
  header: string;
  // eslint-disable-next-line no-unused-vars
  body?(row: T): React.ReactNode;
  // eslint-disable-next-line no-unused-vars
  sortValue?(row: T): string | number | null;
}

interface SearchResultsTableProps<T> {
  records: T[];
  columns: SearchResultsColumn<T>[];
  // eslint-disable-next-line no-unused-vars
  onRowSelect?(row: T): void;
}

const compareValues = (
  a: string | number | null,
  b: string | number | null,
  order: number,
) => {
  // Empty values always sink to the bottom, regardless of direction
  if (a == null || a === "") return b == null || b === "" ? 0 : 1;
  if (b == null || b === "") return -1;
  if (typeof a === "number" && typeof b === "number") return (a - b) * order;
  return (
    String(a).localeCompare(String(b), "en", { sensitivity: "base" }) * order
  );
};

export const SearchResultsTable = <T extends Record<string, unknown>>({
  records,
  columns,
  onRowSelect,
}: SearchResultsTableProps<T>) => {
  const [first, setFirst] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(
    SEARCH_RESULTS_ROWS_PER_PAGE,
  );
  const [sortField, setSortField] = React.useState<string | undefined>(
    undefined,
  );
  const [sortOrder, setSortOrder] = React.useState<1 | 0 | -1 | null | undefined>(
    null,
  );
  const [pageQuery, setPageQuery] = React.useState("");

  // Sorting spans the entire result set, not just the visible page
  const sortedRecords = React.useMemo(() => {
    if (!sortField || !sortOrder) return records;
    const column = columns.find((col) => col.field === sortField);
    const getValue =
      column?.sortValue ??
      ((row: T) => (row[sortField] ?? null) as string | number | null);
    return [...records].sort((a, b) =>
      compareValues(getValue(a), getValue(b), sortOrder),
    );
  }, [records, columns, sortField, sortOrder]);

  const maxFirst =
    sortedRecords.length > 0
      ? Math.floor((sortedRecords.length - 1) / rowsPerPage) * rowsPerPage
      : 0;
  const currentFirst = Math.min(first, maxFirst);
  const pageRecords = sortedRecords.slice(
    currentFirst,
    currentFirst + rowsPerPage,
  );

  // "Search this page" filters only the rows of the current page
  const normalizedQuery = pageQuery.trim().toLowerCase();
  const visibleRecords = normalizedQuery
    ? pageRecords.filter((row) =>
        columns.some((col) =>
          String(row[col.field] ?? "")
            .toLowerCase()
            .includes(normalizedQuery),
        ),
      )
    : pageRecords;

  const onSort = (event: DataTableStateEvent) => {
    setSortField(event.sortField || undefined);
    setSortOrder(event.sortOrder);
  };

  const hasMultiplePages = sortedRecords.length > rowsPerPage;

  return (
    <Styled.TableWrapper>
      <TablePaginationHeader
        first={currentFirst}
        rows={rowsPerPage}
        totalRecords={sortedRecords.length}
        rowsPerPageOptions={SEARCH_RESULTS_ROWS_PER_PAGE_OPTIONS}
        itemsLabel="results"
        spread
        onRowsChange={(rows) => {
          setFirst(0);
          setRowsPerPage(rows);
        }}
      >
        <Styled.PageSearchInput
          type="text"
          value={pageQuery}
          placeholder="Search this page..."
          onChange={(e) => setPageQuery(e.target.value)}
        />
      </TablePaginationHeader>
      <Styled.Table
        value={visibleRecords}
        tableStyle={{ minWidth: "20rem" }}
        lazy
        removableSort
        sortField={sortField}
        sortOrder={sortOrder}
        onSort={onSort}
        sortIcon={(options) => {
          if (options.sorted) {
            if (
              options.sortOrder !== null &&
              options.sortOrder !== undefined &&
              options.sortOrder < 0
            ) {
              return <RevSortIcon />;
            } else {
              return <UpSortIcon />;
            }
          } else {
            return <NoSortIcon />;
          }
        }}
        emptyMessage={
          normalizedQuery
            ? "No matches on this page. Try checking other pages or refining your search."
            : "No results found."
        }
        selectionMode={onRowSelect ? "single" : undefined}
        onSelectionChange={
          onRowSelect
            ? (e: { value: unknown }) => onRowSelect(e.value as T)
            : undefined
        }
      >
        {columns.map((col) => (
          <Column
            key={col.field}
            sortable
            field={col.field}
            header={col.header}
            body={col.body}
          />
        ))}
      </Styled.Table>
      {hasMultiplePages && (
        <TablePaginatorFooter
          first={currentFirst}
          rows={rowsPerPage}
          totalRecords={sortedRecords.length}
          rowsPerPageOptions={SEARCH_RESULTS_ROWS_PER_PAGE_OPTIONS}
          itemsLabel="results"
          onPageChange={(event: PaginatorPageChangeEvent) => {
            setFirst(event.first);
            setRowsPerPage(event.rows);
          }}
        />
      )}
    </Styled.TableWrapper>
  );
};
